import { CriteriaModifier, CriteriaModifierType } from 'api-models'
import { ChangeEvent, Dispatch } from 'react'
import { HelpCircle as RiQuestionLine } from 'components/icons'
import {
	GComboBox,
	GInput,
	GMultiSelect,
	GTooltip
} from 'components/basic-blocks'
import { GCheckbox } from 'components/basic-blocks/g-checkbox'

import { classNames } from 'utils'

import { GBadge } from 'components/basic-blocks/g-badge'
import {
	RenderCountryLabel,
	RenderCountrySelectedLabels
} from 'components/basic-blocks/g-multiselect/utils'
import { CountriesList } from 'config/countries'
import { useStores } from 'hooks'
import { twMerge } from 'tailwind-merge'
import {
	capitalizeFirstLetter,
	capitalizeFirstLetterFromArray
} from 'utils/text'
import {
	ADDITIONAL_CRITERIA_OPTIONS,
	THRESHOLD_OPTIONS
} from '../../../../constants'
import { getCurrentModifier } from '../../../../utils'
import {
	changeConversionModeAction,
	changeThresholdTypeAction,
	setAdditionalCriteriaValueAction,
	setThresholdValueAction,
	toggleAdditionalCriteriaAction,
	toggleAdditionalCriteriaPrefixAction
} from '../../reducer'
import { AdditionalCriteriaProps } from './types'
import {
	getCurrentModifierPrimaryValue,
	getCurrentModifierValue,
	getCurrentModifierValues
} from './utils'
import { HelpCircle as AiOutlineQuestionCircle } from 'components/icons'
export const AdditionalCriteria = ({
	focus,
	modifiers,
	errors,
	threshold,
	dispatch,
	clearError
}: AdditionalCriteriaProps) => {
	const { accountStore } = useStores()
	const { tier } = accountStore
	const isFeatureDisabled = (feature_tier?: string) => {
		if (!feature_tier) return false
		const accountTier = tier()
		return accountTier === 'standard' && feature_tier === 'pro'
	}

	const isBouncedCriteriaDisabled = (key: string) => {
		return key === 'no_device' && focus === 'DEVICE'
	}

	return (
		<section>
			<header className="flex items-center gap-2 mb-4">
				<span className="text-t-base-blue font-medium text-xl">
					Additional criteria
				</span>
				<GTooltip
					position="top"
					arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
					contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
					content="Add additional criteria to focus on specific problems and behaviors of visitors clicking on ads.">
					<AiOutlineQuestionCircle className="ml-2 w-4 h-4 text-t-gray-hold" />
				</GTooltip>
			</header>

			<main className="flex flex-col gap-4">
				{ADDITIONAL_CRITERIA_OPTIONS.map((criteria) => (
					<div key={criteria.key}>
						<div className="flex items-center">
							<GCheckbox
								name={criteria.type}
								label={criteria.label}
								checked={!!getCurrentModifier(modifiers, criteria.type)}
								className="cursor-pointer text-bold"
								onChange={(e) =>
									dispatch(
										toggleAdditionalCriteriaAction(
											criteria.type,
											e.target.checked
										)
									)
								}
								disabled={
									isFeatureDisabled(criteria.feature_tier) ||
									isBouncedCriteriaDisabled(criteria.key)
								}
								labelClassName="font-normal text-t-google-tooltip"
							/>
							<GTooltip
								position="top"
								arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
								contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
								content={
									isBouncedCriteriaDisabled(criteria.key)
										? criteria.disabledTooltip
										: criteria.tooltip
								}
							>
								<AiOutlineQuestionCircle className="ml-2 w-4 h-4 text-t-gray-hold" />
							</GTooltip>
							{isFeatureDisabled(criteria.feature_tier) && (
								<>
									{criteria.feature_tier === 'pro' ? (
										<GBadge
											text="Pro"
											color="purple"
											tooltip="Feature available with Pro subscription"
											className="ml-2"
										/>
									) : (
										<GBadge
											text="Standard"
											color="purple"
											tooltip="Feature available with Standard subscription"
											className="ml-2"
										/>
									)}
								</>
							)}
						</div>
						{criteria.hasPrefix &&
							!!getCurrentModifier(modifiers, criteria.type) && (
								<div className="flex flex-1 gap-2 pl-8 items-baseline mt-2">
									<Prefixes
										criteriaType={criteria.type}
										criteriaKey={criteria.key}
										modifier={getCurrentModifier(modifiers, criteria.type)}
										dispatch={dispatch}
									/>
									{criteria.valueType === 'array' && (
										<GMultiSelect<string>
											name={criteria.key}
											value={getCurrentModifierValues(
												modifiers,
												criteria.type,
												criteria.key
											)}
											options={criteria.options}
											placeholder="Select an option"
											onChange={(val) => {
												dispatch(
													setAdditionalCriteriaValueAction(
														criteria.type,
														criteria.key,
														val
													)
												)
												clearError(criteria.key)
											}}
											renderSelected={(options) => (
												<div>
													{capitalizeFirstLetterFromArray(options).join(', ')}
												</div>
											)}
											renderLabel={(option) => (
												<div className="first-letter:capitalize">{option}</div>
											)}
											className="w-[280px]"
											error={errors ? errors[criteria.key] : undefined}
										/>
									)}
									{criteria.valueType === 'country' && (
										<GMultiSelect<{ code: string | null; name: string }>
											name={criteria.key}
											value={CountriesList.filter((c) =>
												getCurrentModifierValues(
													modifiers,
													criteria.type,
													criteria.key
												)?.includes(c.code)
											)}
											options={CountriesList}
											placeholder={
												CountriesList.find((c) => c.name === 'Global')?.name
											}
											onChange={(val) => {
												dispatch(
													setAdditionalCriteriaValueAction(
														criteria.type,
														criteria.key,
														val.map((v) => v.code)
													)
												)
												clearError(criteria.key)
											}}
											renderSelected={(list) => (
												<RenderCountrySelectedLabels options={list} />
											)}
											renderLabel={(list) => (
												<RenderCountryLabel option={list} />
											)}
											className="w-[280px]"
											error={errors ? errors[criteria.key] : undefined}
										/>
									)}
									{criteria.valueType === 'number' && (
										<div
											className={twMerge(
												'relative',
												criteria.type === CriteriaModifierType.TIME
													? 'w-[40%]'
													: 'flex-1'
											)}
										>
											<GInput
												name={criteria.key}
												placeholder="5"
												inputClassName='font-medium focus:ring-0'
												value={getCurrentModifierPrimaryValue(
													modifiers,
													criteria.type,
													criteria.key
												)}
												type="number"
												min={1}
												onChange={(e: ChangeEvent<HTMLInputElement>) => {
													dispatch(
														setAdditionalCriteriaValueAction(
															criteria.type,
															criteria.key,
															e.target.value
														)
													)
													clearError(criteria.key)
												}}
												className="flex-1 mt-2"
												error={errors ? errors[criteria.key] : undefined}
											/>
											{criteria.type === CriteriaModifierType.TIME && (
												<span className="absolute bg-gray-100 inset-y-0 px-4 py-2 -right-20 rounded-md top-[8px]">
													seconds
												</span>
											)}
										</div>
									)}
									{criteria.valueType === 'string' && (
										<GInput
											name={criteria.key}
											placeholder="Keyword"
											inputClassName='font-medium focus:ring-0'
											value={getCurrentModifierValue(
												modifiers,
												criteria.type,
												criteria.key
											)}
											type="text"
											onChange={(e: ChangeEvent<HTMLInputElement>) => {
												dispatch(
													setAdditionalCriteriaValueAction(
														criteria.type,
														criteria.key,
														e.target.value
													)
												)
												clearError(criteria.key)
											}}
											className="mt-2 w-[280px]"
											error={errors ? errors[criteria.key] : undefined}
										/>
									)}
								</div>
							)}
						{criteria.type === CriteriaModifierType.CONVERSION &&
							!!getCurrentModifier(modifiers, criteria.type) && (
								<div className="flex items-center gap-2">
									<GComboBox
										options={criteria.options}
										filter={(item, query) => item.toString() === query}
										value={
											getCurrentModifier(
												modifiers,
												CriteriaModifierType.CONVERSION
											)?.conversion?.mode
										}
										renderOption={(i) => capitalizeFirstLetter(i.toString())}
										renderValue={(i) => capitalizeFirstLetter(i.toString())}
										onChange={(val) => {
											dispatch(changeConversionModeAction(val))
											clearError(criteria.key)
										}}
										inputClass='text-base h-9'
										error={errors ? errors[criteria.key] : undefined}
										className="pl-8 mt-2 w-full"
									/>
									<div className="w-[260px]">
										{getCurrentModifier(
											modifiers,
											CriteriaModifierType.CONVERSION
										)?.conversion?.mode === 'with label' && (
											<GInput
												name="conversion_label"
												placeholder="App signup"
												inputClassName='font-medium focus:ring-0'
												value={
													getCurrentModifier(
														modifiers,
														CriteriaModifierType.CONVERSION
													)?.conversion?.label
												}
												type="text"
												onChange={(e: ChangeEvent<HTMLInputElement>) => {
													dispatch(
														setAdditionalCriteriaValueAction(
															criteria.type,
															criteria.key,
															e.target.value
														)
													)
													clearError('conversion_label')
												}}
												className="flex-1 mt-2"
												errorPosition="absolute"
												errorClassName="mt-1"
												error={errors ? errors['conversion_label'] : undefined}
											/>
										)}
									</div>
								</div>
							)}
					</div>
				))}
				{modifiers.length > 0 && (
					<div>
						<div className="flex flex-1 items-end gap-2 mb-2">
							<GComboBox
								options={THRESHOLD_OPTIONS}
								label="Criteria threshold"
								filter={(item, query) => item.toString() === query}
								value={THRESHOLD_OPTIONS.find(
									(o) => o.value === threshold.type
								)}
								renderOption={(i) => i.label.toString()}
								renderValue={(i) => i.label.toString()}
								onChange={(val) =>
									dispatch(changeThresholdTypeAction(val.value))
								}
								inputClass='text-base'
								className='w-full'
							/>
							{threshold.type === 'percentage' && (
								<div className="relative">
									<GInput
										placeholder="70"
										value={threshold.percentage_value || 70}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											dispatch(setThresholdValueAction(e.target.value))
										}
										inputClassName='font-medium focus:ring-0'
										type="number"
										min={1}
										className="flex-1"
									/>
									<span className="absolute bg-gray-100 inset-y-0 px-4 py-2 right-[-40px]">
										%
									</span>
								</div>
							)}
						</div>
						{threshold.type === 'percentage' && (
							<span className="text-t-secondary">
								Rule will trigger when {threshold.percentage_value || 70}% of
								triggered clicks match additional criteria.
							</span>
						)}
					</div>
				)}
			</main>
		</section>
	)
}

const Prefixes = (props: {
	criteriaType: CriteriaModifierType
	criteriaKey: string
	modifier?: CriteriaModifier
	dispatch: Dispatch<any>
}) => {
	const { criteriaType, criteriaKey, modifier, dispatch } = props

	const isNegative = modifier ? modifier[criteriaKey].negative : false

	let positivePrefix = 'is'
	let negativePrefix = 'is not'

	if (
		criteriaType === CriteriaModifierType.TIME ||
		criteriaType === CriteriaModifierType.PAGE_VIEWS
	) {
		positivePrefix = 'is more than'
		negativePrefix = 'is less than'
	}

	return (
		<div className="grid grid-cols-2 text-center overflow-auto divide-x-1 rounded-3xl bg-bg-plan-fill font-medium cursor-pointer text-md">
			<span
				className={classNames(
					'px-4 py-2 w-full h-full',
					!isNegative
						? 'bg-purple-gradient text-white rounded-3xl'
						: ' text-t-ad-text'
				)}
				onClick={() =>
					dispatch(
						toggleAdditionalCriteriaPrefixAction(
							criteriaType,
							criteriaKey,
							false
						)
					)
				}
			>
				{positivePrefix}
			</span>
			<span
				className={classNames(
					'px-4 py-2 w-full h-full',
					isNegative
						? 'bg-purple-gradient text-white rounded-3xl'
						: ' text-t-ad-text'
				)}
				onClick={() =>
					dispatch(
						toggleAdditionalCriteriaPrefixAction(
							criteriaType,
							criteriaKey,
							true
						)
					)
				}
			>
				{negativePrefix}
			</span>
		</div>
	)
}
